import { OrniTransportError } from "./transport.ts";
import type { NativeRelayClientDescriptor } from "./remote-terminal-ticket-client.ts";

export interface NativeTerminalMultiplexRelayOptions {
  webSocketFactory?: (url: string) => WebSocket;
  connectTimeoutMs?: number;
  heartbeatIntervalMs?: number;
  heartbeatTimeoutMs?: number;
}

export type NativeTerminalMultiplexMessage =
  | { type: "frame"; streamId: string; body: Record<string, unknown> }
  | { type: "data"; streamId: string; sequence: number; bytes: Uint8Array }
  | { type: "close"; streamId?: string; reason: string }
  | { type: "gap"; streamId: string; requestedAfter: number; latest: number };

export interface NativeTerminalMultiplexStream {
  readonly streamId: string;
  sendFrame(body: Record<string, unknown>): void;
  sendBytes(bytes: Uint8Array): void;
  close(reason?: string): void;
}

interface StreamRecord {
  descriptor: NativeRelayClientDescriptor;
  sequence: number;
  listeners: Set<(message: NativeTerminalMultiplexMessage) => void>;
}

const SOCKET_CONNECTING = 0;
const SOCKET_OPEN = 1;

/**
 * One client WebSocket for one enrolled computer, with several fenced PTY
 * streams. This class is transport-only: it does not know Session semantics,
 * lease payloads, or Provider behavior. Cloud signs each stream credential;
 * the relay validates it when a stream is opened on this existing socket.
 */
export class CloudflareNativeTerminalMultiplexRelay {
  private readonly options: NativeTerminalMultiplexRelayOptions;
  private socket: WebSocket | null = null;
  private connecting: Promise<void> | null = null;
  private helloStreamId: string | null = null;
  private route: Pick<NativeRelayClientDescriptor, "routeId" | "relayUrl" | "epoch"> | null = null;
  private readonly streams = new Map<string, StreamRecord>();
  private closed = false;
  private heartbeatTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimeoutTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(options: NativeTerminalMultiplexRelayOptions = {}) {
    this.options = options;
  }

  async openStream(
    descriptor: NativeRelayClientDescriptor,
    streamId: string,
    onMessage: (message: NativeTerminalMultiplexMessage) => void,
  ): Promise<NativeTerminalMultiplexStream> {
    if (!streamId || new TextEncoder().encode(streamId).byteLength > 160) {
      throw new OrniTransportError("INVALID_ARGUMENT", "Cloudflare relay stream id is invalid.");
    }
    if (this.closed) throw new OrniTransportError("UNAVAILABLE", "Cloudflare relay is closed.", true);
    const existing = this.streams.get(streamId);
    if (existing) {
      existing.listeners.add(onMessage);
      return this.streamHandle(streamId, onMessage);
    }
    await this.ensureConnected(descriptor, streamId);
    if (!this.sameRoute(descriptor)) {
      throw new OrniTransportError("CONFLICT", "Cloudflare relay route changed while opening the stream.", true);
    }
    // Concurrent observer/controller acquisition can both arrive before the
    // first socket handshake completes. Re-check after the shared await so a
    // later continuation cannot replace the first listener set.
    const connectedExisting = this.streams.get(streamId);
    if (connectedExisting) {
      connectedExisting.listeners.add(onMessage);
      return this.streamHandle(streamId, onMessage);
    }
    const record: StreamRecord = {
      descriptor,
      sequence: 0,
      listeners: new Set([onMessage]),
    };
    this.streams.set(streamId, record);
    // The first stream is authenticated by the hello token. Subsequent
    // streams must present their own Cloud-signed credential in the open
    // frame; the Worker never trusts a client-supplied stream id alone.
    this.sendJson({
      type: "open",
      streamId,
      ...(this.helloStreamId === streamId ? {} : { credential: descriptor.clientToken }),
    });
    return this.streamHandle(streamId, onMessage);
  }

  removeListener(streamId: string, listener: (message: NativeTerminalMultiplexMessage) => void): void {
    const record = this.streams.get(streamId);
    if (!record) return;
    record.listeners.delete(listener);
    if (!record.listeners.size) this.closeStream(streamId, "last listener released");
  }

  closeStream(streamId: string, reason = "stream closed"): void {
    if (!this.streams.has(streamId)) return;
    if (this.socket?.readyState === SOCKET_OPEN) {
      this.sendJson({ type: "close", streamId, reason });
    }
    this.streams.delete(streamId);
  }

  close(code = 1000, reason = "relay closed"): void {
    this.closed = true;
    this.stopHeartbeat();
    this.connecting = null;
    this.streams.clear();
    this.helloStreamId = null;
    this.route = null;
    if (this.socket && (this.socket.readyState === SOCKET_OPEN || this.socket.readyState === SOCKET_CONNECTING)) {
      this.socket.close(code, reason);
    }
    this.socket = null;
  }

  private streamHandle(streamId: string, listener: (message: NativeTerminalMultiplexMessage) => void): NativeTerminalMultiplexStream {
    return {
      streamId,
      sendFrame: (body) => this.sendFrame(streamId, body),
      sendBytes: (bytes) => this.sendBytes(streamId, bytes),
      close: (reason) => {
        const record = this.streams.get(streamId);
        if (!record) return;
        // Observer and controller adapters share a stream. Closing one
        // adapter removes only its listener; the stream closes after the last
        // adapter releases it.
        record.listeners.delete(listener);
        if (!record.listeners.size) this.closeStream(streamId, reason);
      },
    };
  }

  private sendFrame(streamId: string, body: Record<string, unknown>): void {
    this.assertStream(streamId);
    this.sendJson({ type: "frame", streamId, body });
  }

  private sendBytes(streamId: string, bytes: Uint8Array): void {
    const record = this.assertStream(streamId);
    if (!bytes.byteLength || bytes.byteLength > 64 * 1024) {
      throw new OrniTransportError("INVALID_ARGUMENT", "Cloudflare relay frame is outside the size limit.");
    }
    if (!this.socket || this.socket.readyState !== SOCKET_OPEN) {
      throw new OrniTransportError("UNAVAILABLE", "Cloudflare relay is not connected.", true);
    }
    record.sequence += 1;
    this.socket.send(encodeDataFrame(2, streamId, record.sequence, bytes));
  }

  private assertStream(streamId: string): StreamRecord {
    const record = this.streams.get(streamId);
    if (!record) throw new OrniTransportError("CONFLICT", "Cloudflare relay stream is not open.", true);
    return record;
  }

  private async ensureConnected(descriptor: NativeRelayClientDescriptor, streamId: string): Promise<void> {
    if (this.socket?.readyState === SOCKET_OPEN) {
      if (this.sameRoute(descriptor)) return;
      this.disconnectForRouteChange();
    }
    if (this.connecting) {
      await this.connecting;
      if (this.sameRoute(descriptor)) return;
      this.disconnectForRouteChange();
    }
    const task = new Promise<void>((resolve, reject) => {
      const factory = this.options.webSocketFactory ?? ((url: string) => new WebSocket(url));
      const socket = factory(descriptor.relayUrl);
      socket.binaryType = "arraybuffer";
      this.socket = socket;
      this.route = {
        routeId: descriptor.routeId,
        relayUrl: descriptor.relayUrl,
        epoch: descriptor.epoch,
      };
      let settled = false;
      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        socket.close(1000, "relay handshake timeout");
        reject(new OrniTransportError("TIMEOUT", "Cloudflare relay handshake timed out.", true));
      }, Math.max(25, this.options.connectTimeoutMs ?? 10_000));
      socket.onopen = () => {
        this.helloStreamId = streamId;
        socket.send(JSON.stringify({
          type: "hello",
          role: "client",
          token: descriptor.clientToken,
          epoch: descriptor.epoch,
          clientId: `multiplex-${crypto.randomUUID()}`,
        }));
      };
      socket.onmessage = (event) => {
        try {
          if (event.data === HEARTBEAT_RESPONSE) {
            this.acceptHeartbeat();
            return;
          }
          const message = decodeMultiplexMessage(event.data, descriptor.epoch);
          if (message.type === "hello_ack") {
            clearTimeout(timer);
            if (!settled) {
              settled = true;
              resolve();
            }
            this.scheduleHeartbeat();
            return;
          }
          if (message.type === "frame" || message.type === "data" || message.type === "close" || message.type === "gap") {
            const streamId = message.streamId;
            if (!streamId) return;
            const record = this.streams.get(streamId);
            if (record) for (const listener of record.listeners) listener(message);
          }
        } catch (cause) {
          clearTimeout(timer);
          const error = cause instanceof Error ? cause : new Error("Invalid Cloudflare relay frame.");
          if (!settled) {
            settled = true;
            reject(error);
          }
          socket.close(1008, error.message);
        }
      };
      socket.onerror = () => {
        clearTimeout(timer);
        const error = new OrniTransportError("UNAVAILABLE", "Cloudflare relay connection failed.", true);
        if (!settled) {
          settled = true;
          reject(error);
        }
      };
      socket.onclose = (event) => {
        clearTimeout(timer);
        this.stopHeartbeat();
        const error = new OrniTransportError("UNAVAILABLE", event.reason || "Cloudflare relay closed.", true);
        if (!settled) {
          settled = true;
          reject(error);
        }
        if (this.socket !== socket) return;
        this.socket = null;
        this.connecting = null;
        this.helloStreamId = null;
        this.route = null;
        const records = Array.from(this.streams.values());
        this.streams.clear();
        for (const record of records) {
          for (const listener of record.listeners) listener({ type: "close", reason: error.message });
        }
      };
    });
    this.connecting = task;
    try {
      await task;
    } finally {
      if (this.connecting === task) this.connecting = null;
    }
  }

  private sendJson(value: Record<string, unknown>): void {
    if (!this.socket || this.socket.readyState !== SOCKET_OPEN) {
      throw new OrniTransportError("UNAVAILABLE", "Cloudflare relay is not connected.", true);
    }
    const encoded = JSON.stringify(value);
    if (new TextEncoder().encode(encoded).byteLength > 128 * 1024) {
      throw new OrniTransportError("INVALID_ARGUMENT", "Cloudflare relay control frame is too large.");
    }
    this.socket.send(encoded);
  }

  private sameRoute(descriptor: NativeRelayClientDescriptor): boolean {
    return this.route?.routeId === descriptor.routeId
      && this.route.relayUrl === descriptor.relayUrl
      && this.route.epoch === descriptor.epoch;
  }

  private disconnectForRouteChange(): void {
    this.stopHeartbeat();
    const socket = this.socket;
    this.socket = null;
    this.connecting = null;
    this.helloStreamId = null;
    this.route = null;
    const records = Array.from(this.streams.values());
    this.streams.clear();
    for (const record of records) {
      for (const listener of record.listeners) {
        listener({ type: "close", reason: "Cloudflare relay route changed." });
      }
    }
    if (socket && (socket.readyState === SOCKET_OPEN || socket.readyState === SOCKET_CONNECTING)) {
      socket.close(1000, "relay route changed");
    }
  }

  private scheduleHeartbeat(): void {
    this.stopHeartbeat();
    const interval = Math.max(25, this.options.heartbeatIntervalMs ?? DEFAULT_HEARTBEAT_INTERVAL_MS);
    this.heartbeatTimer = setTimeout(() => {
      this.heartbeatTimer = null;
      const socket = this.socket;
      if (!socket || socket.readyState !== SOCKET_OPEN) return;
      socket.send(HEARTBEAT_REQUEST);
      const timeout = Math.max(25, this.options.heartbeatTimeoutMs ?? DEFAULT_HEARTBEAT_TIMEOUT_MS);
      this.heartbeatTimeoutTimer = setTimeout(() => {
        this.heartbeatTimeoutTimer = null;
        if (this.socket !== socket || socket.readyState !== SOCKET_OPEN) return;
        socket.close(4000, "relay heartbeat timeout");
      }, timeout);
      unrefTimer(this.heartbeatTimeoutTimer);
    }, interval);
    unrefTimer(this.heartbeatTimer);
  }

  private acceptHeartbeat(): void {
    if (this.heartbeatTimeoutTimer !== null) clearTimeout(this.heartbeatTimeoutTimer);
    this.heartbeatTimeoutTimer = null;
    this.scheduleHeartbeat();
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer !== null) clearTimeout(this.heartbeatTimer);
    if (this.heartbeatTimeoutTimer !== null) clearTimeout(this.heartbeatTimeoutTimer);
    this.heartbeatTimer = null;
    this.heartbeatTimeoutTimer = null;
  }
}

const HEARTBEAT_REQUEST = '{"type":"heartbeat"}';
const HEARTBEAT_RESPONSE = '{"type":"heartbeat_ack"}';
const DEFAULT_HEARTBEAT_INTERVAL_MS = 15_000;
const DEFAULT_HEARTBEAT_TIMEOUT_MS = 8_000;
function unrefTimer(timer: ReturnType<typeof setTimeout>): void {
  (timer as unknown as { unref?: () => void }).unref?.();
}

type DecodedMessage =
  | { type: "hello_ack"; epoch: number }
  | NativeTerminalMultiplexMessage;

function decodeMultiplexMessage(value: unknown, expectedEpoch: number): DecodedMessage {
  if (value instanceof ArrayBuffer) {
    const bytes = new Uint8Array(value);
    if (bytes.byteLength < 12) throw new Error("Relay data frame is truncated.");
    const view = new DataView(value);
    const kind = view.getUint8(0);
    const version = view.getUint8(1);
    const streamLength = view.getUint16(2);
    if (kind !== 1 || version !== 1 || streamLength === 0 || 12 + streamLength >= bytes.byteLength) {
      throw new Error("Relay data frame is invalid.");
    }
    const streamId = new TextDecoder().decode(bytes.slice(12, 12 + streamLength));
    return {
      type: "data",
      streamId,
      sequence: Number(view.getBigUint64(4)),
      bytes: bytes.slice(12 + streamLength),
    };
  }
  if (typeof value !== "string") throw new Error("Relay message is invalid.");
  const parsed = JSON.parse(value) as Record<string, unknown>;
  if (parsed.type === "hello_ack" && Number(parsed.epoch) === expectedEpoch) {
    return { type: "hello_ack", epoch: Number(parsed.epoch) };
  }
  if (parsed.type === "frame" && typeof parsed.streamId === "string" && parsed.body && typeof parsed.body === "object") {
    return { type: "frame", streamId: parsed.streamId, body: parsed.body as Record<string, unknown> };
  }
  if (parsed.type === "gap" && typeof parsed.streamId === "string") {
    return { type: "gap", streamId: parsed.streamId, requestedAfter: Number(parsed.requestedAfter), latest: Number(parsed.latest) };
  }
  if (parsed.type === "close" && typeof parsed.reason === "string") {
    return { type: "close", streamId: typeof parsed.streamId === "string" ? parsed.streamId : undefined, reason: parsed.reason };
  }
  throw new Error("Relay control frame is invalid.");
}

function encodeDataFrame(kind: 1 | 2, streamId: string, sequence: number, bytes: Uint8Array): ArrayBuffer {
  const stream = new TextEncoder().encode(streamId);
  const output = new Uint8Array(12 + stream.byteLength + bytes.byteLength);
  const view = new DataView(output.buffer);
  view.setUint8(0, kind);
  view.setUint8(1, 1);
  view.setUint16(2, stream.byteLength);
  view.setBigUint64(4, BigInt(sequence));
  output.set(stream, 12);
  output.set(bytes, 12 + stream.byteLength);
  return output.buffer;
}
